import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, User, Phone, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatAxiosError } from '../lib/api';
import GoogleSignInButton from '../components/GoogleSignInButton';

export default function SignupPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ name: '', email: '', mobile: '', password: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await register(form);
      const next = location.state?.from || '/dashboard';
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatAxiosError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page" data-testid="signup-page">
      <div className="auth-card">
        <Link to="/" className="auth-back" data-testid="back-to-home">
          <ArrowLeft size={16} /> Back to home
        </Link>
        <h1 className="auth-title">Create your account</h1>
        <p className="auth-subtitle">It&apos;s free. Start with a discovery call or browse all sessions.</p>

        <GoogleSignInButton label="Sign up with Google" />
        <div className="auth-divider"><span>or</span></div>

        <form onSubmit={onSubmit} className="auth-form">
          <div className="auth-field">
            <label>Full name</label>
            <div className="auth-input-wrap">
              <User size={16} />
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={onChange}
                required
                placeholder="Your name"
                data-testid="signup-name"
              />
            </div>
          </div>
          <div className="auth-field">
            <label>Email</label>
            <div className="auth-input-wrap">
              <Mail size={16} />
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                required
                placeholder="you@example.com"
                data-testid="signup-email"
              />
            </div>
          </div>
          <div className="auth-field">
            <label>Mobile number</label>
            <div className="auth-input-wrap">
              <Phone size={16} />
              <input
                type="tel"
                name="mobile"
                value={form.mobile}
                onChange={onChange}
                required
                minLength={6}
                placeholder="+91 99999 99999"
                data-testid="signup-mobile"
              />
            </div>
          </div>
          <div className="auth-field">
            <label>Password</label>
            <div className="auth-input-wrap">
              <Lock size={16} />
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={onChange}
                required
                minLength={6}
                placeholder="At least 6 characters"
                data-testid="signup-password"
              />
            </div>
          </div>

          {error && <div className="auth-error" data-testid="signup-error">{error}</div>}

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary btn-large btn-full"
            data-testid="signup-submit"
          >
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <p className="auth-foot">
          Already have an account?{' '}
          <Link to="/login" state={location.state} data-testid="link-to-login">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
